import { REDUCED_MOTION_MEDIA_QUERY } from '@packages/shared-ui';
import { makeStyles } from '@/styles/theme/tss-react';

import { darkOrLightStyle } from '@/utils/styles';

interface Props {
  isOnDark: boolean | undefined;
}

const useStyles = makeStyles<Props>({
  name: 'MobileNav',
})(
  (
    {
      brandColors,
      type,
      breakpoints,
      zLevels,
      spacing,
      typography: { pxToRem },
    },
    props
  ) => ({
    navBodyMobileSelectedItem: {
      position: 'relative',
      borderBottom: `4px solid ${brandColors.primaryMain}`,
      zIndex: zLevels.fixed,
    },
    selectedItemDark: {
      borderBottomColor: brandColors.white,
    },
    mobileNavItemWrapper: {
      '&:first-of-type': {
        paddingTop: spacing(2),
      },
    },
    navGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing(-1),
      flexShrink: 0,
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      margin: 0,
    },
    mobileNavItem: {
      cursor: 'pointer',
      padding: `${pxToRem(12)} ${spacing(3)} ${pxToRem(12)} ${spacing(3)}`,
      flexDirection: 'row',
      alignItems: 'center',

      position: 'relative',
      justifyContent: 'space-between',
      display: 'flex',
      width: '100vw',
      ...type['20_medium'],
      height: pxToRem(56),
    },
    mobilePromoRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      padding: pxToRem(8),
    },
    mobilePromoRowLight: {
      background: `linear-gradient(270deg, rgba(116, 107, 26, 0.12) 0%, rgba(116, 107, 26, 0.06) 100.08%)`,
    },
    mobilePromoRowDark: {
      background: `linear-gradient(270deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100.08%)`,
    },
    mobilePromoRowSolidDark: {
      background: brandColors.tintsHeroBlue10,
    },
    // Renders as a Tertiary Button (link styling); color comes from isOnDark.
    mobilePromoLink: {
      '&&': {
        whiteSpace: 'normal',
      },
      '& p': {
        margin: 0,
        textAlign: 'center',
        ...type['12_regular'],
      },
    },
    mobileNavCtaItem: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
    },
    navBodyCollapsed: {
      padding: `${spacing(-1)} 0`,
    },
    mobileNavDropdown: {
      zIndex: zLevels.dropdown,
      position: 'fixed',
      display: 'flex',
      flexDirection: 'row',
      top: pxToRem(60),
      bottom: 0,
      left: 0,
      right: '-100%',
      background: 'rgba(255, 252, 245, 1)',
      transform: 'translateX(0%)',
      transition: 'transform .25s',
      [REDUCED_MOTION_MEDIA_QUERY]: {
        transition: 'none',
      },
      overflowY: 'scroll',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      scrollbarWidth: 'none',
    },
    mobileNavDropdownDark: {
      background: brandColors.tintsHeroBlue10,
    },
    mobileNavLeftSide: {
      width: '50%',
      transition: 'width .25s',
      [REDUCED_MOTION_MEDIA_QUERY]: {
        transition: 'none',
      },
      paddingTop: spacing(1),
      marginTop: spacing(1),
    },
    mobileNavRightSide: {
      width: '50%',
      background: brandColors.tintsOffWhite60,
      zIndex: zLevels.popover,
      position: 'relative',
    },
    mobileNavLinksView: {
      width: '0%',
      transition: 'width .25s',
      [REDUCED_MOTION_MEDIA_QUERY]: {
        transition: 'none',
      },
      overflow: 'hidden',
      paddingBottom: pxToRem(16),
    },
    mobileDropdownInfo: {
      padding: ` ${spacing(2)} ${spacing(3)} ${spacing(3)} ${spacing(3)}`,
      [breakpoints.up('md')]: {
        display: 'none',
      },
      '& > div': {
        borderTop: '1px solid var(--line, rgba(0, 0, 0, 0.20))',
        paddingTop: pxToRem(16),
      },
    },
    utilityLink: {
      ...type['14_medium'],
      color: brandColors.mediumBlue,
      minHeight: pxToRem(48),
      padding: `${pxToRem(12)} ${pxToRem(8)}`,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: pxToRem(6),
      textDecoration: 'none',
      '& svg': {
        flexShrink: 0,
        width: pxToRem(24),
        height: pxToRem(24),
        marginRight: 0,
      },
    },
    mobileDrawerUtility: {
      '&& svg': {
        marginRight: 0,
        width: pxToRem(24),
        height: pxToRem(24),
      },
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    // Renders the phone utility as a Tertiary Button (link styling); left-aligned
    // with a comfortable tap target. Color comes from isOnDark.
    mobileDrawerPhoneButton: {
      '&&': {
        justifyContent: 'flex-start',
        minHeight: pxToRem(48),
        gap: pxToRem(6),
      },
      '& p': {
        margin: 0,
        ...type['14_medium'],
      },
    },
    utilityLinkDark: {
      color: brandColors.tintsChargeBlue60,
    },
    hamburgerIconContainer: {
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: pxToRem(44),
      height: pxToRem(44),
      padding: pxToRem(10),
      color: brandColors.primaryMain,
      flexShrink: 0,
      '& svg': {
        width: pxToRem(24),
        height: pxToRem(24),
      },
    },
    t1ParentPageLabelContainer: {
      flex: 1,
      justifyContent: 'center',
      display: 'flex',
      '& h3': { ...type['18_medium'] },
    },
    locationDisplay: {
      color: darkOrLightStyle(props)(
        brandColors.tintsChargeBlue60,
        brandColors.mediumBlue
      ),
    },
  })
);

export default useStyles;
